import Header from '@/layout/components/Header'
import './ProductLandingPage.style.less'
import {
  ExportOutlined,
  FilterFilled,
  LeftOutlined,
  MoreOutlined,
  PlusOutlined,
  RightOutlined,
  SearchOutlined,
  TagOutlined,
  UndoOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  Avatar,
  Button,
  Image,
  Input,
  notification,
  Pagination,
  Space,
  Spin,
  Table,
} from 'antd'
import type { ColumnsType } from 'antd/lib/table'
import debounce from 'lodash/debounce'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import PLACE_HOLDER from '@/assets/images/place-holder.jpg'
import ProductCard from '../components/ProductCard/ProductCard'
import EmptyProductPlaceHolder from '../components/EmptyProductPlaceHolder/EmptyProductPlaceHolder'
import { useQuery } from 'react-query'
import { deleteProduct, getProduct } from '../ProductTransform'
import type { Product, ProductMedia } from '../ProductTransform'
import ProductDeleteModal from '../components/ProductDeleteModal/ProductDeleteModal'
import { useProductPermission } from '../ProductPermission'
import { useAuth } from '@/providers/auth.provider'
import {
  COMPANY_SETTING_PRODUCT_ADVANCE_FILTER_CUSTOM,
  USER_ROLE,
} from '@/constant/user'
import { useExportData } from '@/hook/useExportData'
import { ExportConfirmModal } from '@/components/ExportConfirmModal/ExportConfirmModal'
import { PBF_QUERY_KEY } from '@/constant/pbf'
import {
  convertKeysToCamelCase,
  dateRangConverter,
  getUrlParams,
  handleAdvanceFilterQueryParams,
  isEmptyValue,
  removeUrlParams,
  setUrlParams,
  transformFromUrlQueryToDateRange,
} from '@/utils/common'
import {
  get,
  isEmpty,
  keyBy,
  keys,
  omitBy,
  pick,
  size,
  snakeCase,
  sortBy,
} from 'lodash'
import DrawerAdvanceFilter from '@/screens/QSF/Inspection/ListInspection/components/DrawerAdvanceFilter'
import {
  PRODUCT_PRESET_SORTER,
  DEFAULT_PRODUCT_FILTER,
  PRODUCT_ADVANCE_FILTER_LISTING,
  PRODUCT_PAYLOAD_TYPE,
} from '../Product.constant'
import { getCompanySetting } from '@/screens/SystemConfigure/UserManagment/UserManagment.transform'
import ProductActionMenu from '../components/ProductActionMenu/ProductActionMenu'
import { useFreshProductSetting } from '../ProductSettingQuery'
import { IMG_PLACE_HOLDER } from '@/constant/common'

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 10
const PRODUCT_PAGE_SIZE_STORAGE_KEY = 'product_page_size'
const SEARCH_DEBOUNCE_MS = 500
const filterIcon = <FilterFilled style={{ fontSize: '19px' }} />
const undoIcon = <UndoOutlined style={{ fontSize: '19px' }} />

function getStoredProductPageSize() {
  try {
    const pageSize = Number(localStorage.getItem(PRODUCT_PAGE_SIZE_STORAGE_KEY))

    return Number.isInteger(pageSize) && pageSize > 0
      ? pageSize
      : DEFAULT_PAGE_SIZE
  } catch {
    return DEFAULT_PAGE_SIZE
  }
}

function storeProductPageSize(pageSize: number) {
  try {
    localStorage.setItem(PRODUCT_PAGE_SIZE_STORAGE_KEY, String(pageSize))
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

function getPositivePage(value: string | null) {
  const page = Number(value)

  return Number.isInteger(page) && page > 0 ? page : DEFAULT_PAGE
}
function getProductPropertyImage(product: Product) {
  if (
    product.picture &&
    typeof product.picture === 'object' &&
    product.picture.url_path
  ) {
    return product.picture.thumbnail_url_path || product.picture.url_path
  }

  const imageProperty = product.property_values?.find((propertyValue) => {
    return (
      propertyValue.data_type === 'image' &&
      propertyValue.field === 'productImage' &&
      propertyValue.value
    )
  })

  if (!imageProperty?.value) {
    return PLACE_HOLDER
  }

  try {
    const parsedValue = JSON.parse(imageProperty.value) as ProductMedia[]
    const firstImage = parsedValue[0]

    return (
      firstImage?.thumbnail_url_path || firstImage?.url_path || PLACE_HOLDER
    )
  } catch {
    return PLACE_HOLDER
  }
}

function ProductLandingPage() {
  const { user } = useAuth()
  const {
    canCreateProduct,
    canDeleteProduct,
    canEditProduct,
    canExportProducts,
    canViewProduct,
  } = useProductPermission(user)
  const isSupplierUser =
    user?.subscription?.role_access?.role === USER_ROLE.SUPPLIER

  const exportData = useExportData({
    exportKey: 'products',
    queryKey: PBF_QUERY_KEY.PRODUCT_EXPORT_SETTING,
    enabled: canExportProducts(),
  })

  const [searchParams, setSearchParams] = useSearchParams()

  const currentPage = getPositivePage(searchParams.get('page'))

  const [tableSort, setTableSort] = useState(getUrlParams('sort') ?? '')
  const [tmpSearch, setTmpSearch] = useState(getUrlParams('search') ?? '')
  const getInitialSorterState = () => {
    const sort = getUrlParams('sort') ?? ''
    if (!sort) return { field: null, order: null }

    const order = sort.startsWith('-') ? 'descend' : 'ascend'
    const sortValue = sort.startsWith('-') ? sort.slice(1) : sort

    const presetSorter = PRODUCT_PRESET_SORTER.find(
      (s) => s.ascend === sortValue || s.descend === sortValue
    )

    if (presetSorter) {
      return { field: presetSorter.field, order }
    }
    return { field: null, order: null }
  }
  const [sorterState, setsorterState] = useState<any>(getInitialSorterState())
  const [isOpenAdvanceFilter, setIsOpenAdvaceFilter] = useState(false)

  const onOpenCloseAdvanceFilterFN = () => {
    setIsOpenAdvaceFilter(!isOpenAdvanceFilter)
  }

  const {
    data: settingProductAdvanceCustom,
    isFetched: isProductAdvanceCustomFetched,
  } = useQuery(['PRODUCT_ADVANCE_FILTER_CUSTOM_COLUMN'], async () => {
    const setting = await getCompanySetting(
      '',
      COMPANY_SETTING_PRODUCT_ADVANCE_FILTER_CUSTOM
    )
    if (!setting) return []

    const getColumns = sortBy(
      get(setting, 'value.columns', []),
      'display_order'
    )
    const assignedObject = keyBy(getColumns, 'field')

    return assignedObject
  })
  const getCustomAdvanceFilterKeys = useMemo(
    () => keys(settingProductAdvanceCustom),
    [settingProductAdvanceCustom]
  )

  const getCompleteFilter = () => ({
    ...DEFAULT_PRODUCT_FILTER,
    ...getCustomAdvanceFilterKeys.reduce(
      (acc, key) => ({ ...acc, [key]: null }),
      {}
    ),
  })
  const [stateFilter, setStateFilter] = useState(getCompleteFilter())
  const [pageSize, setPageSize] = useState(getStoredProductPageSize)
  const [deleteTargetProduct, setDeleteTargetProduct] =
    useState<Product | null>(null)
  const [isDeletingProduct, setIsDeletingProduct] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    const params = new URLSearchParams(searchParams)
    let shouldUpdateParams = false
    const normalizedPage = getPositivePage(params.get('page'))

    if (params.get('page') !== String(normalizedPage)) {
      params.set('page', String(normalizedPage))
      shouldUpdateParams = true
    }

    if (!params.has('search')) {
      params.set('search', '')
      shouldUpdateParams = true
    }

    if (!params.has('filter')) {
      params.set('filter', '')
      shouldUpdateParams = true
    }

    if (shouldUpdateParams) {
      setSearchParams(params)
    }
  }, [searchParams, setSearchParams])

  const { data: productSetting } = useFreshProductSetting()
  const dataProductSettings = productSetting?.fieldNames

  const mapColumnNameToCustomField = (
    columnName: string,
    fallbackName: string
  ) => {
    if (!Array.isArray(dataProductSettings)) {
      return fallbackName
    }

    const matchedField = dataProductSettings.find(
      (field) => field.field === columnName
    )

    return matchedField?.display_name || fallbackName
  }

  const productQuery = useQuery(
    ['products', currentPage, pageSize, tableSort, stateFilter],
    async () => {
      const queryParams: any = {
        page_number: currentPage,
        page_size: pageSize,
      }
      if (tableSort) queryParams.sort = tableSort
      const filterPayload = omitBy(
        stateFilter,
        (val) => val === null || val === '' || isEmpty(val)
      )
      const response = await getProduct(queryParams, filterPayload)
      return response
    },
    {
      keepPreviousData: true,
    }
  )

  const navigateToAddProductPage = () => {
    navigate('add')
  }

  const products = productQuery.data?.products ?? []
  const visibleProducts = useMemo(() => {
    return products.filter((product) => canViewProduct(product))
  }, [canViewProduct, products])
  const pagination = productQuery.data?.pagination
  const totalRecord =
    visibleProducts.length === products.length
      ? pagination?.total_record ?? 0
      : visibleProducts.length

  const hasActiveFilters = Object.entries(stateFilter).some(([key, value]) => {
    if (key === 'search') return false
    return !isEmptyValue(value)
  })

  const shouldShowEmptyProductPlaceholder =
    !productQuery.isFetching &&
    isEmptyValue(stateFilter.search) &&
    !hasActiveFilters &&
    visibleProducts.length === 0 &&
    canCreateProduct()

  const onPaginationChange = (page: number, nextPageSize: number) => {
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize)
      storeProductPageSize(nextPageSize)
      onSetPage(1)
      return
    }

    onSetPage(page)
  }

  const onNavigateToProductDetailPage = (productId: string) => {
    navigate(productId)
  }

  const onSearch = debounce((event) => {
    onSetPage(1)
    handleTableChange({}, { ...stateFilter, search: event.target.value })
  }, 500)
  const onSetPage = (page: number) => {
    navigate({ search: `?${setUrlParams('page', String(page))}` })
  }

  const onSort = (sorter: any) => {
    if (sorter && sorter.order) {
      let sortValue = sorter.field

      const presetSorter = PRODUCT_PRESET_SORTER.find(
        (s) => s.field === sorter.field
      )

      if (presetSorter) {
        sortValue =
          sorter.order === 'descend'
            ? presetSorter.descend
            : presetSorter.ascend
      } else {
        if (sorter.order === 'descend') {
          sortValue = `-${sorter.field}`
        }
      }

      navigate({ search: `?${setUrlParams('sort', sortValue)}` })
      setTableSort(sortValue)
      setsorterState({ field: sorter.field, order: sorter.order })
    } else {
      navigate({ search: `?${removeUrlParams('sort')}` })
      setTableSort('')
      setsorterState({ field: null, order: null })
    }
  }

  const handleSetStateFilters = (newState: any) => {
    setStateFilter((prevState: any) =>
      convertKeysToCamelCase(
        { ...prevState, ...newState },
        getCustomAdvanceFilterKeys
      )
    )
  }

  const handleTableChange = (pagination: any, filters: any) => {
    const { current = 1 } = pagination
    const filterData = { ...filters }

    const dateCustomFields = getCustomAdvanceFilterKeys.filter(
      (key) => get(settingProductAdvanceCustom, `${key}.type`) === 'date'
    )
    dateCustomFields.forEach((camelKey) => {
      const snakeKey = snakeCase(camelKey)
      if (filterData[snakeKey]) {
        const [strDate] = dateRangConverter(filterData[snakeKey])
        filterData[snakeKey] = Array.isArray(strDate)
          ? strDate.join(',')
          : strDate
      }
    })

    const allAcceptedQueries = [
      ...PRODUCT_ADVANCE_FILTER_LISTING,
      ...getCustomAdvanceFilterKeys,
    ] as string[]

    const cvCamelCaseQueryParams = convertKeysToCamelCase(
      filterData,
      getCustomAdvanceFilterKeys
    )

    handleAdvanceFilterQueryParams(cvCamelCaseQueryParams, {
      queriesAccepted: allAcceptedQueries,
      navigate,
    })

    onSetPage(current)

    handleSetStateFilters(filterData)
  }

  const handleOnSubmitFilter = (dataFilter: PRODUCT_PAYLOAD_TYPE) => {
    handleTableChange({ current: 1 }, dataFilter)
  }

  const handleResetFilter = () => {
    const completeFilter = {
      ...DEFAULT_PRODUCT_FILTER,
      ...getCustomAdvanceFilterKeys.reduce(
        (acc, key) => ({ ...acc, [key]: null }),
        {}
      ),
    }
    handleTableChange({ current: 1 }, completeFilter)
    setTmpSearch('')
  }

  const handleFilterParamsUrl = () => {
    const splitFN = (key: string) => {
      const urlValue = getUrlParams(key)
      if (!urlValue) return null

      const isDateField =
        get(settingProductAdvanceCustom, `${key}.type`) === 'date'
      if (isDateField) return urlValue

      const isTextField =
        get(settingProductAdvanceCustom, `${key}.type`) === 'text'
      if (isTextField) return urlValue

      return urlValue.split(',')
    }
    const getParams: any = getCompleteFilter()

    const allAcceptedQueries = [
      ...PRODUCT_ADVANCE_FILTER_LISTING,
      ...getCustomAdvanceFilterKeys,
    ]

    for (let key of allAcceptedQueries) {
      const val = splitFN(key as string)
      if (val) getParams[key] = val
    }
    const urlSearch = getUrlParams('search')
    if (urlSearch) getParams['search'] = urlSearch

    handleSetStateFilters(getParams)
  }
  useEffect(() => {
    if (isProductAdvanceCustomFetched) {
      handleFilterParamsUrl()
    }
  }, [isProductAdvanceCustomFetched, getCustomAdvanceFilterKeys])

  const onDeleteProduct = async () => {
    if (!deleteTargetProduct || !canDeleteProduct(deleteTargetProduct)) return

    try {
      setIsDeletingProduct(true)
      await deleteProduct(deleteTargetProduct.id)
      notification.success({
        message: 'Product deleted',
        duration: 3,
        placement: 'top',
      })
      setDeleteTargetProduct(null)
      await productQuery.refetch()
    } catch (error: any) {
      notification.error({
        message: error?.response?.data?.message || 'Product cannot be deleted',
        duration: 4,
        placement: 'top',
      })
    } finally {
      setIsDeletingProduct(false)
    }
  }

  const productColumns: ColumnsType<Product> = [
    {
      key: 'number',
      title: mapColumnNameToCustomField('number', 'PRODUCT NO.'),
      dataIndex: 'number',
      width: 240,
      sorter: true,
      sortOrder: sorterState.field === 'number' ? sorterState.order : null,
      render: (_, product) => (
        <Space size="middle">
          <Image
            className="product-image"
            fallback={IMG_PLACE_HOLDER}
            src={
              typeof product.picture === 'string'
                ? product.picture
                : product.picture?.url_path ?? ''
            }
            alt={product.name || product.number || 'Product'}
          />
          <Link className="product-number-link" to={product.id}>
            {product.number || '-'}
          </Link>
        </Space>
      ),
    },
    {
      key: 'name',
      title: mapColumnNameToCustomField('name', 'NAME'),
      dataIndex: 'name',
      sorter: true,
      sortOrder: sorterState.field === 'name' ? sorterState.order : null,
      render: (name: string) => name || '-',
    },
    {
      key: 'supplier',
      title: mapColumnNameToCustomField('supplier', 'SUPPLIER'),
      dataIndex: 'supplier',
      sorter: true,
      sortOrder: sorterState.field === 'supplier' ? sorterState.order : null,
      render: (_, product) =>
        product.supplier?.name || product.factory?.name || '-',
    },
    {
      title: mapColumnNameToCustomField('created_by', 'CREATOR'),
      render: (_, product) => {
        const firstName = product.created_by?.first_name ?? ''
        const lastName = product.created_by?.last_name ?? ''
        const name = `${firstName} ${
          lastName ? lastName.split('')[0] + '.' : ''
        }`.trim()

        return (
          <Space size="middle">
            <Avatar
              icon={<UserOutlined />}
              src={product.created_by?.avatar?.url_path}
            />
            <span>{name || '-'}</span>
          </Space>
        )
      },
    },
    {
      title: 'ACTION',
      width: 96,
      render: (_, product) => {
        const canEditCurrentProduct = canEditProduct(product)
        const canDeleteCurrentProduct = canDeleteProduct(product)
        return (
          <ProductActionMenu
            canDelete={canDeleteCurrentProduct}
            canEdit={canEditCurrentProduct}
            menuClassName="action-button-product-landing-page-container"
            onDelete={() => setDeleteTargetProduct(product)}
            onEdit={() => navigate(`${product.id}/edit`)}
            showDisabledWhenEmpty
            triggerClassName="button-more"
          />
        )
      },
    },
  ]

  const toolbarClassName = `products-toolbar ${
    canExportProducts() && (exportData.exportConfigs?.length ?? 0) > 0
      ? 'products-toolbar-with-export'
      : ''
  }`.trim()

  const hasAdvancedFilters = !!size(omitBy(stateFilter, isEmpty))
  const isEmptyFiltering =
    !hasAdvancedFilters && isEmptyValue(stateFilter['search'])

  const sortingDataFilter = pick(stateFilter, [
    ...PRODUCT_ADVANCE_FILTER_LISTING,
    ...getCustomAdvanceFilterKeys,
  ])

  const drawerDataFilter = useMemo(() => {
    const result: any = { ...sortingDataFilter }
    const dateCustomFields = getCustomAdvanceFilterKeys.filter(
      (key) => get(settingProductAdvanceCustom, `${key}.type`) === 'date'
    )
    dateCustomFields.forEach((key) => {
      if (typeof result[key] === 'string' && result[key]) {
        const strDates = result[key].split(',') as [string, string]
        result[key] = transformFromUrlQueryToDateRange(strDates)
      }
    })
    return result
  }, [sortingDataFilter])

  return (
    <>
      <Header
        icon={<TagOutlined className="icon" />}
        title={'My Products'}
        background="linear-gradient(181.35deg, #b1579c 1.15%,#da7ac4 98.85%)"
      />

      <div className="products-landing-page-container">
        {shouldShowEmptyProductPlaceholder ? (
          <div className="products-empty-state-container">
            <EmptyProductPlaceHolder
              onCreateProduct={navigateToAddProductPage}
            />
          </div>
        ) : (
          <div className="products-landing-page-body-container">
            {/* Product status tiles are disabled until the API is ready.
            <div className="products-status-tiles">
              {productStatusTiles.map((tile) => (
                <div
                  className={`products-status-tile ${tile.className}`}
                  key={tile.label}
                >
                  <div className="products-status-tile-icon">{tile.icon}</div>
                  <div className="products-status-tile-content">
                    <span className="label">{tile.label}</span>
                    <strong className="count-text">{tile.count}</strong>
                  </div>
                  <img
                    className="products-status-tile-watermark"
                    src={FINGERPRINT_ICON}
                    alt=""
                  />
                </div>
              ))}
            </div>
            */}
            <div className={toolbarClassName}>
              <div className="products-toolbar-left">
                <Input
                  key={tmpSearch}
                  placeholder="Search"
                  prefix={<SearchOutlined />}
                  defaultValue={tmpSearch}
                  onChange={onSearch}
                />
                <Button
                  className={`button-advance-filter ${
                    isEmptyFiltering && 'grey-bg'
                  }`}
                  size="large"
                  type="primary"
                  icon={filterIcon}
                  onClick={onOpenCloseAdvanceFilterFN}
                />
                <Button
                  className="button-clear-filter"
                  disabled={isEmptyFiltering}
                  type="link"
                  icon={undoIcon}
                  onClick={handleResetFilter}
                >
                  Clear
                </Button>
              </div>

              <div className="products-toolbar-actions">
                {canExportProducts() &&
                  exportData.exportConfigs?.map((item) => (
                    <Button
                      key={item.name}
                      className="export-excel-button"
                      icon={<ExportOutlined />}
                      onClick={() => exportData.handleExportClick(item)}
                      loading={exportData.isExporting}
                    >
                      {item.title}
                    </Button>
                  ))}
                {canCreateProduct() && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={navigateToAddProductPage}
                  >
                    Create Product
                  </Button>
                )}
              </div>
            </div>

            <div className="products-mobile-list">
              <Spin spinning={productQuery.isFetching}>
                {visibleProducts.length > 0 ? (
                  visibleProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      description={product.name || '-'}
                      hideSupplier={isSupplierUser}
                      imageSrc={getProductPropertyImage(product)}
                      itemNo={product.number || '-'}
                      onClick={() => onNavigateToProductDetailPage(product.id)}
                      supplier={
                        product.supplier?.name || product.factory?.name || '-'
                      }
                    />
                  ))
                ) : (
                  <></>
                )}
              </Spin>
            </div>

            <div className="products-desktop-table">
              <Table
                sticky
                className="table-container table-left product-table"
                columns={productColumns}
                dataSource={visibleProducts}
                loading={productQuery.isFetching}
                pagination={{
                  current: currentPage,
                  pageSize,
                  total: totalRecord,
                  showSizeChanger: true,
                  showLessItems: true,
                  onChange: onPaginationChange,
                }}
                onChange={(_, __, sorter: any) => {
                  onSort(sorter)
                }}
                rowKey="id"
                scroll={{
                  x: 720,
                }}
                locale={{
                  emptyText: '',
                }}
              />
            </div>

            <div className="products-pagination">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalRecord}
                responsive={false}
                showLessItems
                itemRender={(_, type, originalElement) => {
                  if (type === 'prev' || type === 'next') {
                    return type === 'prev' ? (
                      <LeftOutlined />
                    ) : (
                      <RightOutlined />
                    )
                  }
                  return originalElement
                }}
                showSizeChanger
                onChange={onPaginationChange}
              />
            </div>
          </div>
        )}
      </div>
      {deleteTargetProduct && canDeleteProduct(deleteTargetProduct) && (
        <ProductDeleteModal
          open={Boolean(deleteTargetProduct)}
          productName={
            deleteTargetProduct.number || deleteTargetProduct.name || ''
          }
          loading={isDeletingProduct}
          onCancel={() => setDeleteTargetProduct(null)}
          onDelete={onDeleteProduct}
        />
      )}
      <ExportConfirmModal
        open={exportData.isModalOpen}
        onClose={exportData.closeModal}
        onConfirm={exportData.handleConfirmExport}
        title={exportData.selectedItem?.title || 'Export Data'}
        loading={exportData.isExporting}
        error={exportData.exportError}
      />
      {isOpenAdvanceFilter && (
        <DrawerAdvanceFilter
          isOpen={isOpenAdvanceFilter}
          onClose={onOpenCloseAdvanceFilterFN}
          masterData={{}}
          dataSource={{}}
          dataFilter={drawerDataFilter}
          onSubmit={handleOnSubmitFilter}
          drawerMode="myProducts"
          options={{
            customTitle: {
              number: {
                name: mapColumnNameToCustomField('number', 'Product Number'),
              },
              name: {
                name: mapColumnNameToCustomField('name', 'Product Name'),
              },
              suppliers: {
                name: mapColumnNameToCustomField('supplier', 'Supplier'),
              },

              ...settingProductAdvanceCustom,
            },
          }}
        />
      )}
    </>
  )
}

export default ProductLandingPage
